import { Card } from "@/components/ui/card";
import { Heart, Medal, Sparkles, Flower } from "lucide-react";
import { type Quiz as BaseQuiz } from "@/lib/quiz-api";
import { motion } from "framer-motion";
import { fadeIn, slideInFromBottom } from "@/lib/animations";
import { QuizTheme } from "@/lib/theme-presets";
import { cn } from "@/lib/utils";

// Extend the Quiz type to include theme
interface Quiz extends BaseQuiz {
  theme: QuizTheme;
}

interface QuizResultCardProps {
  quiz: Quiz;
  playerName: string;
  score: number;
  totalQuestions: number;
}

export default function QuizResultCard({ 
  quiz, 
  playerName, 
  score, 
  totalQuestions 
}: QuizResultCardProps) {
  const percentage = Math.round((score / totalQuestions) * 100);
  
  // Debug
  console.log('QuizResultCard - Score:', score, 'Total Questions:', totalQuestions, 'Percentage:', percentage);
  
  // Pick the right icon based on the quiz theme
  const ResultIcon = () => {
    switch(quiz.theme.welcomeIcon) {
      case 'hearts': return (
        <div className="relative flex items-center justify-center">
          <Heart 
            className="h-16 w-16 drop-shadow-md mx-auto" 
            style={{ color: quiz.theme.primaryColor }}
            fill="currentColor"
          />
          {percentage >= 70 && (
            <motion.div 
              className="absolute -top-4 -right-4"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <Medal className="h-8 w-8 text-yellow-400 drop-shadow-md" />
            </motion.div>
          )}
        </div>
      );
      case 'rings': return (
        <div className="relative flex items-center justify-center">
          <Sparkles 
            className="h-16 w-16 drop-shadow-md mx-auto" 
            style={{ color: quiz.theme.primaryColor }}
            fill="currentColor"
          />
          {percentage >= 70 && (
            <motion.div 
              className="absolute -top-4 -right-4"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <Medal className="h-8 w-8 text-yellow-400 drop-shadow-md" />
            </motion.div>
          )}
        </div>
      );
      case 'doves': return (
        <div className="relative flex items-center justify-center">
          <Flower 
            className="h-16 w-16 drop-shadow-md mx-auto" 
            style={{ color: quiz.theme.primaryColor }}
            fill="currentColor"
          />
          {percentage >= 70 && (
            <motion.div 
              className="absolute -top-4 -right-4"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <Medal className="h-8 w-8 text-yellow-400 drop-shadow-md" />
            </motion.div>
          )}
        </div>
      );
      default: return (
        <div className="relative flex items-center justify-center">
          <Heart 
            className="h-16 w-16 drop-shadow-md mx-auto" 
            style={{ color: quiz.theme.primaryColor }}
            fill="currentColor"
          />
          {percentage >= 70 && (
            <motion.div 
              className="absolute -top-4 -right-4"
              initial={{ opacity: 0, scale: 0 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ delay: 0.5, type: "spring" }}
            >
              <Medal className="h-8 w-8 text-yellow-400 drop-shadow-md" />
            </motion.div>
          )}
        </div>
      );
    }
  };

  // Background patterns based on score
  const renderPatterns = () => {
    // Only render decorative patterns for higher scores
    if (percentage < 50) return null;
    
    return (
      <>
        <div className="absolute top-6 left-6 opacity-10 pointer-events-none">
          <svg width="40" height="40" viewBox="0 0 24 24" fill={quiz.theme.primaryColor} xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
        <div className="absolute bottom-6 right-6 opacity-10 pointer-events-none">
          <svg width="40" height="40" viewBox="0 0 24 24" fill={quiz.theme.primaryColor} xmlns="http://www.w3.org/2000/svg">
            <path d="M12 21.35l-1.45-1.32C5.4 15.36 2 12.28 2 8.5 2 5.42 4.42 3 7.5 3c1.74 0 3.41.81 4.5 2.09C13.09 3.81 14.76 3 16.5 3 19.58 3 22 5.42 22 8.5c0 3.78-3.4 6.86-8.55 11.54L12 21.35z" />
          </svg>
        </div>
      </>
    );
  };

  // Get appropriate message based on score
  const getResultMessage = () => {
    if (percentage >= 90) return "Perfect! You know them so well!";
    if (percentage >= 70) return "Amazing! You're a great match!";
    if (percentage >= 50) return "Good job! You're getting to know them!";
    if (percentage >= 30) return "Nice try! Keep learning about them!";
    return "Don't worry! There's more to learn about them!";
  };

  return (
    <motion.div
      initial="hidden"
      animate="visible"
      variants={fadeIn}
    >
      <Card 
        className="p-8 sm:p-10 max-w-md mx-auto bg-white shadow-xl relative overflow-hidden border-2" 
        id="quiz-result-card"
        style={{ 
          borderColor: `${quiz.theme.primaryColor}40`,
          background: `linear-gradient(135deg, white, ${quiz.theme.secondaryColor}30)`
        }}
      >
        {/* Background elements */}
        {renderPatterns()}
        
        {/* Top right decorative dot pattern */}
        <div className="absolute top-0 right-0 w-20 h-20 opacity-5 pointer-events-none">
          <svg width="80" height="80" viewBox="0 0 80 80" xmlns="http://www.w3.org/2000/svg">
            <circle cx="10" cy="10" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="30" cy="10" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="50" cy="10" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="70" cy="10" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="10" cy="30" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="30" cy="30" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="50" cy="30" r="3" fill={quiz.theme.primaryColor} />
            <circle cx="70" cy="30" r="3" fill={quiz.theme.primaryColor} />
          </svg>
        </div>
        
        <div className="text-center space-y-4 relative z-10">
          <motion.div 
            className="flex justify-center items-center mb-4"
            variants={slideInFromBottom}
          >
            {ResultIcon()}
          </motion.div>

          <motion.h2 
            className="text-2xl sm:text-3xl font-serif font-medium"
            style={{ color: quiz.theme.primaryColor }}
            variants={slideInFromBottom}
          >
            Great Job!
          </motion.h2>

          <motion.div 
            className="space-y-4 py-4"
            variants={slideInFromBottom}
          >
            <p className="text-lg sm:text-xl font-medium text-gray-700 bg-white bg-opacity-60 py-1 px-4 rounded-full inline-block">
              {playerName}
            </p>
            
            {/* Score display with circular progress */}
            <div className="relative mx-auto w-48 h-48 flex items-center justify-center bg-white bg-opacity-90 rounded-full border-4 shadow-lg"
              style={{ borderColor: `${quiz.theme.primaryColor}` }}>
              <div className="absolute inset-0">
                <svg className="w-full h-full" viewBox="0 0 100 100">
                  <circle 
                    cx="50" cy="50" r="42" 
                    fill="none" 
                    stroke="#f3f4f6" 
                    strokeWidth="10"
                  />
                  <circle 
                    cx="50" cy="50" r="42" 
                    fill="none" 
                    stroke={quiz.theme.primaryColor} 
                    strokeWidth="10"
                    strokeDasharray={`${percentage * 2.64} 264`}
                    strokeLinecap="round"
                    style={{ transform: "rotate(-90deg)", transformOrigin: "center" }}
                  />
                </svg>
              </div>
              
              {/* Percentage display */}
              <div className="flex items-center justify-center text-center z-10 bg-white bg-opacity-50 rounded-full h-28 w-28">
                <span className="font-bold" 
                  style={{ 
                    color: quiz.theme.primaryColor,
                    fontSize: "3rem",
                    textShadow: "0 0 5px rgba(255,255,255,0.8)"
                  }}>
                  {percentage}%
                </span>
              </div>
            </div>
            
            <p className="text-gray-600 font-medium">
              {score} correct out of {totalQuestions} questions
            </p>
            
            <p 
              className="text-lg font-medium mt-4 bg-white bg-opacity-60 py-2 px-4 rounded-lg inline-block shadow-sm"
              style={{ color: quiz.theme.primaryColor }}
            >
              {getResultMessage()}
            </p>
          </motion.div>
        </div>
      </Card>
    </motion.div>
  );
}